import { Button, Callout } from '@aecom-ai-engineering/react-components';
import { AudioLines, FileText, Hourglass, RefreshCw, Upload } from 'lucide-react';
import { FC, ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { RegenerateWithAudioDialog } from './regenerate-with-audio-dialog';
import { UploadTranscriptDialog } from './upload-transcript-dialog';

import type { TranscriptBannerState } from '../utils/transcript-quality';
import type { UploadErrorDetails } from '../utils/transcript-upload';

const T_PREFIX = 'meeting-secretary.meetings.';

interface TranscriptStatusBannerProps {
  state: TranscriptBannerState;
  organizerEmail?: string;
  onCheckAgain: () => void;
  onUpload: (file: File) => Promise<boolean>;
  isUploading: boolean;
  /** An i18n key. */
  uploadError: string | null;
  uploadErrorDetails: UploadErrorDetails;
  onClearUploadError: () => void;
  onRegenerate: () => void;
  isRegenerating: boolean;
  /** Something is still being generated from the text that is about to be replaced. */
  regenerateDisabled: boolean;
}

/**
 * The one message the meeting shows above its tabs about its transcript: how good it is, that it
 * is still on its way, or that an upload is the last resort. Owns the two dialogs its buttons
 * open.
 */
export const TranscriptStatusBanner: FC<TranscriptStatusBannerProps> = ({
  state,
  organizerEmail,
  onCheckAgain,
  onUpload,
  isUploading,
  uploadError,
  uploadErrorDetails,
  onClearUploadError,
  onRegenerate,
  isRegenerating,
  regenerateDisabled,
}) => {
  const { t } = useTranslation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isRegenerateOpen, setIsRegenerateOpen] = useState(false);

  const limitedBody = t(`${T_PREFIX}transcript-banner-limited-body`);

  // The header is its own bold line, the body follows in the secondary text colour, and the
  // action sits inside the banner, centred against its height.
  const callout = (
    variant: 'info' | 'warning',
    icon: typeof FileText,
    header: string,
    body: ReactNode,
    action?: ReactNode,
    inlineHeader = false,
  ) => (
    <Callout variant={variant} icon={icon} data-testid="transcript-banner">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="min-w-0">
          <strong className={inlineHeader ? 'font-semibold' : 'block font-semibold'}>
            {header}
          </strong>
          {inlineHeader && ' '}
          <span className="text-[var(--ds-color-text-secondary)]">{body}</span>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </Callout>
  );

  switch (state) {
    case 'full-quality':
      return callout(
        'info',
        AudioLines,
        t(`${T_PREFIX}transcript-banner-full-quality-label`),
        t(`${T_PREFIX}transcript-banner-full-quality`),
        undefined,
        true,
      );

    case 'limited-organizer':
      return (
        <>
          {callout(
            'warning',
            FileText,
            t(`${T_PREFIX}transcript-banner-limited-label`),
            <>
              {limitedBody} {t(`${T_PREFIX}transcript-banner-limited-organizer`)}
            </>,
            <Button
              variant="solid"
              size="md"
              disabled={regenerateDisabled || isRegenerating}
              aria-busy={isRegenerating}
              onClick={() => setIsRegenerateOpen(true)}
            >
              <RefreshCw size={16} aria-hidden="true" />
              {t(`${T_PREFIX}transcript-regenerate`)}
            </Button>,
          )}
          <RegenerateWithAudioDialog
            open={isRegenerateOpen}
            onClose={() => setIsRegenerateOpen(false)}
            onConfirm={() => {
              setIsRegenerateOpen(false);
              onRegenerate();
            }}
          />
        </>
      );

    case 'limited-upload':
      return callout(
        'warning',
        FileText,
        t(`${T_PREFIX}transcript-banner-limited-upload-label`),
        limitedBody,
      );

    case 'limited-no-audio':
      return callout(
        'warning',
        FileText,
        t(`${T_PREFIX}transcript-banner-limited-no-audio-label`),
        limitedBody,
      );

    case 'limited-teams':
      return callout(
        'warning',
        FileText,
        t(`${T_PREFIX}transcript-banner-limited-label`),
        limitedBody,
      );

    case 'limited-participant':
      return callout(
        'warning',
        FileText,
        t(`${T_PREFIX}transcript-banner-limited-label`),
        <>
          {limitedBody}{' '}
          {organizerEmail ? (
            <Trans
              i18nKey={`${T_PREFIX}transcript-banner-limited-participant`}
              values={{ email: organizerEmail }}
              components={{
                email: (
                  <a
                    href={`mailto:${organizerEmail}`}
                    className="font-medium text-[var(--ds-color-text-primary)] underline"
                  >
                    {organizerEmail}
                  </a>
                ),
              }}
            />
          ) : (
            t(`${T_PREFIX}transcript-banner-limited-participant-no-email`)
          )}
        </>,
      );

    case 'waiting':
      return callout(
        'info',
        Hourglass,
        t(`${T_PREFIX}transcript-banner-waiting-label`),
        t(`${T_PREFIX}transcript-banner-waiting`),
        <Button variant="solid" size="md" onClick={onCheckAgain}>
          <RefreshCw size={16} aria-hidden="true" />
          {t(`${T_PREFIX}transcript-banner-check-again`)}
        </Button>,
      );

    case 'upload-required':
      return (
        <>
          {callout(
            'warning',
            Upload,
            t(`${T_PREFIX}transcript-banner-upload-label`),
            t(`${T_PREFIX}transcript-banner-upload`),
            <Button variant="solid" size="md" onClick={() => setIsUploadOpen(true)}>
              <Upload size={16} aria-hidden="true" />
              {t(`${T_PREFIX}transcript-upload-button`)}
            </Button>,
          )}
          <UploadTranscriptDialog
            open={isUploadOpen}
            onClose={() => setIsUploadOpen(false)}
            onUpload={onUpload}
            isUploading={isUploading}
            errorKey={uploadError}
            errorDetails={uploadErrorDetails}
            onClearError={onClearUploadError}
          />
        </>
      );
  }
};
